import { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import { Patient, CreatePatientData } from "@/services/patient-service";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { InsuranceSelect } from "@/components/shared/InsuranceSelect";
import { onlyNumbers } from "@/utils/formatters";

interface CreateEditPatientDialogProps {
  patient: Patient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CreatePatientData, id?: string) => void;
}

export function CreateEditPatientDialog({
  patient, open, onOpenChange, onSave,
}: CreateEditPatientDialogProps) {
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [email, setEmail] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [insuranceId, setInsuranceId] = useState("");

  const cityName = "Rio de Janeiro";
  const stateCode = "RJ";

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (patient) {
      setName(patient.name);
      setCpf(patient.cpf);
      setBirthDate(patient.birth_date);
      setEmail(patient.email);
      setCellPhone(patient.cellphone);
      setCardNumber(patient.card_number || "");
      setInsuranceId(patient.dental_insurance_id || "");
    } else {
      clearForm();
    }
  }, [patient, open]);

  const clearForm = () => {
    setName(""); setCpf(""); setBirthDate(""); setEmail("");
    setCellPhone(""); setCardNumber(""); setInsuranceId(""); setErrors({});
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Nome é obrigatório";
    const cleanCpf = onlyNumbers(cpf);
    if (cleanCpf.length !== 11) newErrors.cpf = "CPF inválido";
    if (!birthDate) newErrors.birthDate = "Data de nascimento é obrigatória";
    else if (birthDate === "1980-01-01") newErrors.birthDate = "Data de nascimento inválida — confirme com o paciente";
    if (!email.trim()) newErrors.email = "Email é obrigatório";
    const cleanCellPhone = onlyNumbers(cellPhone);
    if (cleanCellPhone.length < 10 || cleanCellPhone.length > 11)
      newErrors.cellPhone = "Telefone inválido";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => document.getElementById(Object.keys(newErrors)[0])?.focus(), 0);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const data: CreatePatientData = {
      name, cpf: onlyNumbers(cpf), birth_date: birthDate, email,
      cellphone: onlyNumbers(cellPhone), postcode: null,
      city_name: cityName, state_code: stateCode,
      card_number: cardNumber.trim() ? cardNumber : null,
      dental_insurance_id: insuranceId.trim() ? insuranceId : null,
    };
    try {
      await onSave(data, patient?.id);
      onOpenChange(false);
    } catch (error) {
      // Erros de salvamento ficam a cargo do pai
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide">
            {patient ? "Editar Paciente" : "Novo Paciente"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="filter-label" htmlFor="name">Nome *</Label>
            <Input className="filter-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome completo" aria-invalid={!!errors.name} />
            {errors.name && <p className="text-red-600 text-xs">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cpf">CPF *</Label>
              <InputMask mask="999.999.999-99" value={cpf} onChange={(e) => setCpf(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cpf" placeholder="000.000.000-00" aria-invalid={!!errors.cpf} />}
              </InputMask>
              {errors.cpf && <p className="text-red-600 text-xs">{errors.cpf}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="birthDate">Data de Nascimento *</Label>
              <Input className="filter-input" id="birthDate" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} aria-invalid={!!errors.birthDate} />
              {errors.birthDate && <p className="text-red-600 text-xs">{errors.birthDate}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="email">Email *</Label>
              <Input className="filter-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" aria-invalid={!!errors.email} />
              {errors.email && <p className="text-red-600 text-xs">{errors.email}</p>}
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cellPhone">Telefone *</Label>
              <InputMask mask="(99) 99999-9999" value={cellPhone} onChange={(e) => setCellPhone(e.target.value)}>
                {(inputProps: any) => <Input className="filter-input" {...inputProps} id="cellPhone" placeholder="(00) 00000-0000" aria-invalid={!!errors.cellPhone} />}
              </InputMask>
              {errors.cellPhone && <p className="text-red-600 text-xs">{errors.cellPhone}</p>}
            </div>
          </div>

          <input type="hidden" value={cityName} />
          <input type="hidden" value={stateCode} />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="insurance">Convênio Dental</Label>
              <InsuranceSelect value={insuranceId} onValueChange={setInsuranceId} />
            </div>
            <div className="space-y-1">
              <Label className="filter-label" htmlFor="cardNumber">Número da Carteirinha</Label>
              <Input className="filter-input" id="cardNumber" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="Número da carteirinha" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t gap-2">
          {Object.keys(errors).length > 0 && (
            <p className="text-red-600 text-xs">Corrija os campos marcados em vermelho.</p>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button size="sm" className="btn-header" onClick={handleSave}>
              {patient ? "Salvar Alterações" : "Criar Paciente"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
